/**
 * Feature types for the MD3 Expressive Shape Engine.
 * Ported from androidx.graphics.shapes.Features.kt
 *
 * Features describe the outline segments of a RoundedPolygon:
 * - Corner: a (potentially rounded) vertex
 * - Edge: a straight or curved segment between two corners
 */

import type { Cubic } from "../math/cubic";
import type { PointTransformer } from "../utils/utils";

/**
 * A discriminated union representing one outline segment of a polygon.
 *
 * @example
 * const corner: Feature = { type: "corner", cubics: [...], convex: true };
 * const edge: Feature   = { type: "edge",   cubics: [...] };
 */
export type Feature = CornerFeature | EdgeFeature;

/** A (potentially rounded) corner vertex. */
export interface CornerFeature {
	readonly type: "corner";
	/**
	 * Cubic curves making up this corner.
	 * Unrounded corner = 1 zero-length cubic. Rounded = 1–3 cubics (flanking + arc + flanking).
	 */
	readonly cubics: Cubic[];
	/** True if this corner is convex (pointing outward). */
	readonly convex: boolean;
}

/** A straight or curved edge segment between two corners. */
export interface EdgeFeature {
	readonly type: "edge";
	/** Cubic curves making up this edge (usually a single straight-line cubic). */
	readonly cubics: Cubic[];
}

/**
 * Creates a corner feature.
 * @param cubics - Cubic curves defining the corner shape
 * @param convex - Whether the corner is convex
 */
export function cornerFeature(cubics: Cubic[], convex: boolean): CornerFeature {
	return { type: "corner", cubics, convex };
}

/**
 * Creates an edge feature.
 * @param cubics - Cubic curves defining the edge
 */
export function edgeFeature(cubics: Cubic[]): EdgeFeature {
	return { type: "edge", cubics };
}

/**
 * Returns a transformed copy of a feature.
 * @param feature - The feature to transform
 * @param fn - Point transformer function
 */
export function transformFeature(
	feature: Feature,
	fn: PointTransformer,
): Feature {
	const cubics = feature.cubics.map((c) => c.transformed(fn));
	if (feature.type === "corner") {
		return cornerFeature(cubics, feature.convex);
	}
	return edgeFeature(cubics);
}
